import { useState, useCallback, type FormEvent } from 'react';
import { ApiClientError } from '../services/api-client';
import { updateDividend } from '../services/dividend-service';
import {
  validateDividendUpdateInput,
  type ValidationError,
} from '@covered-calls/shared';
import type { DividendRecord, DividendUpdateInput } from '@covered-calls/shared';

/**
 * EditDividendDialog — edit an existing cash dividend recorded against a
 * position's underlying stock. Pre-populated from the selected record and
 * submitted via PUT /api/trades/{id}/dividends/{dividendId} through
 * updateDividend.
 *
 * Simpler than AddProtectivePutDialog: a dividend has no commission and no
 * broker-profile handling. The stored `amount` is DERIVED
 * (perShare × quantity), shown here as a live read-only preview. The record's
 * `version` is carried through as `expectedVersion` for optimistic locking; a
 * 409 surfaces a "changed elsewhere, reload" message.
 *
 * Validates: Requirements 6.1, 6.3, 6.4, 6.6
 */

export interface EditDividendDialogProps {
  positionId: string;
  ticker: string;
  quantity: number;
  dividend: DividendRecord;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  perShare: string;
  paymentDate: string;
}

export function EditDividendDialog({
  positionId,
  ticker,
  quantity,
  dividend,
  onSuccess,
  onCancel,
}: EditDividendDialogProps) {
  const [form, setForm] = useState<FormState>({
    perShare: String(dividend.perShare),
    paymentDate: dividend.paymentDate,
  });
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Live read-only preview of the derived amount (perShare × quantity, 2dp).
  const perShareNum = parseFloat(form.perShare);
  const amountPreview = Number.isFinite(perShareNum) ? perShareNum * quantity : 0;

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => prev.filter((err) => err.field !== field));
      setServerError(null);
    },
    []
  );

  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const input: DividendUpdateInput = {
        perShare: parseFloat(form.perShare),
        paymentDate: form.paymentDate,
        expectedVersion: dividend.version,
      };

      const validationErrors = validateDividendUpdateInput(input);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);
      setIsSubmitting(true);

      try {
        await updateDividend(positionId, dividend.dividendId, input);
        onSuccess();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.status === 409) {
            setServerError(
              'This dividend was changed elsewhere. Please reload and try again.'
            );
          } else if (error.errors && error.errors.length > 0) {
            setErrors(error.errors.map((err) => ({ field: err.field, message: err.message })));
          } else {
            setServerError(error.message || 'An error occurred. Please try again.');
          }
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, positionId, dividend.dividendId, dividend.version, onSuccess]
  );

  return (
    <div
      role="dialog"
      aria-labelledby="edit-dividend-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="edit-dividend-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Edit Dividend — {ticker}
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Update the cash dividend received on your {quantity.toLocaleString()} shares of{' '}
          {ticker}. The amount is derived from the per-share rate (a credit).
        </p>

        {serverError && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Per Share */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="edit-dividend-per-share"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Per Share ($ per share)
            </label>
            <input
              id="edit-dividend-per-share"
              type="number"
              step="0.01"
              min="0.01"
              max="999999.99"
              value={form.perShare}
              onChange={handleFieldChange('perShare')}
              aria-invalid={!!getFieldError('perShare')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('perShare') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('perShare') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('perShare')}</span>
            )}
          </div>

          {/* Payment Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="edit-dividend-payment-date"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Payment Date
            </label>
            <input
              id="edit-dividend-payment-date"
              type="date"
              value={form.paymentDate}
              onChange={handleFieldChange('paymentDate')}
              aria-invalid={!!getFieldError('paymentDate')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('paymentDate') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('paymentDate') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('paymentDate')}</span>
            )}
          </div>

          {/* Amount (read-only, derived from per-share rate × holding) */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Amount</label>
            <input
              type="text"
              value={`$${amountPreview.toFixed(2)}`}
              readOnly
              disabled
              data-testid="edit-dividend-amount-preview"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-surface-2)',
                color: 'var(--color-muted)',
              }}
            />
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              Derived from the per-share rate × {quantity.toLocaleString()} shares.
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-muted)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
